import { useState, useMemo } from 'react';
import { cartService } from '@/features/cart/services/cartService';
import type { CartResponse, CartItemResponse, CartItemStatus } from '@/features/cart/types';
import { ItemStatusCode } from '@/features/cart/types';

export interface UsePriceConfirmationReturn {
  itemsNeedingPriceConfirmation: CartItemResponse[];
  confirmingAll: boolean;
  confirmAllPrices: () => Promise<void>;
}

export function usePriceConfirmation(
  cart: CartResponse | null,
  itemStatuses: Record<string, CartItemStatus | null>,
  setItemStatuses: React.Dispatch<React.SetStateAction<Record<string, CartItemStatus | null>>>
): UsePriceConfirmationReturn {
  const [confirmingAll, setConfirmingAll] = useState(false);

  // Get items that need price confirmation
  const itemsNeedingPriceConfirmation = useMemo(() => {
    if (!cart?.items) return [];
    return cart.items.filter(item => {
      const status = itemStatuses[item.id];
      return status?.item_status_code === ItemStatusCode.PriceUpdateRequired;
    });
  }, [cart?.items, itemStatuses]);

  // Handle confirming all prices
  const handleConfirmAllPrices = async () => {
    if (itemsNeedingPriceConfirmation.length === 0) return;
    
    setConfirmingAll(true);
    try {
      const BATCH_SIZE = 5;
      const items = itemsNeedingPriceConfirmation;
      
      // Confirm items in batches
      for (let i = 0; i < items.length; i += BATCH_SIZE) {
        const batch = items.slice(i, i + BATCH_SIZE);
        
        // Confirm prices for this batch
        const confirmPromises = batch.map(item =>
          cartService.confirmCartItemPrice(item.id).catch(error => {
            console.error(`Failed to confirm price for item ${item.id}:`, error);
            return null;
          })
        );
        
        await Promise.allSettled(confirmPromises);
        
        // Refetch statuses for this batch
        const statusPromises = batch.map(async (item) => {
          try {
            const status = await cartService.getCartItemStatus(item.id);
            return { itemId: item.id, status: status.status };
          } catch (error) {
            console.error(`Failed to fetch status for item ${item.id}:`, error);
            return { itemId: item.id, status: null };
          }
        });
        
        const results = await Promise.allSettled(statusPromises);
        setItemStatuses(prev => {
          const updated = { ...prev };
          results.forEach((result) => {
            if (result.status === 'fulfilled') {
              const { itemId, status } = result.value;
              updated[itemId] = status;
            }
          });
          return updated;
        });
        
        // Small delay between batches
        if (i + BATCH_SIZE < items.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    } catch (error) {
      console.error('Error confirming all prices:', error);
    } finally {
      setConfirmingAll(false);
    }
  };

  return {
    itemsNeedingPriceConfirmation,
    confirmingAll,
    confirmAllPrices: handleConfirmAllPrices,
  };
}

